import { useState } from 'react';

const faqs = [
  {
    q: "Who installs fencing near me in St. Mary's, Georgia?",
    a: "Masters Fencing is St. Mary's most trusted local fencing contractor, serving Camden County for over 20 years. We install chain-link, wood, steel, vinyl, and aluminum fencing for residential and commercial properties throughout the area. Call 912-409-2031 for a free on-site estimate today.",
    cat: 'Local',
  },
  {
    q: "How much does a fence cost in St. Mary's, GA?",
    a: "Fencing costs vary by material, height, and footage. Wood typically runs $15–$40 per linear foot; vinyl and aluminum $20–$50+; chain-link from $10–$20. We offer completely free on-site estimates so you get an accurate quote for your specific property — transparent pricing, no surprises.",
    cat: 'Pricing',
  },
  {
    q: 'What is the best type of fence for privacy?',
    a: "For maximum privacy, solid cedar board-on-board wood fencing and vinyl privacy panels are our top picks for Camden County homes. Both provide full visual coverage, withstand Georgia's humidity well, and meaningfully increase property value. We help you choose based on your budget and maintenance preference.",
    cat: 'Materials',
  },
  {
    q: 'How long does fence installation take?',
    a: "Most residential fencing projects are completed in one to two days. Larger commercial perimeters may take three to five days. Our crew communicates a clear schedule upfront, works efficiently, and consistently finishes on time — minimizing disruption to your daily life.",
    cat: 'Timeline',
  },
  {
    q: 'Do you provide free estimates for fencing in Georgia?',
    a: "Yes — Masters Fencing offers completely free, no-obligation on-site estimates throughout St. Mary's and Camden County. We visit your property, assess the full project, and deliver transparent pricing. Sign on the day of your estimate and receive $100 off your total installation cost.",
    cat: 'Estimates',
  },
  {
    q: 'What areas in Georgia do you serve?',
    a: "We serve St. Mary's, King's Bay, Highland Oaks, Sugar Mill Plantation, Kingsland, River Cove, Woodbine, White Oak, Homeland, and the greater Camden County region of Southeast Georgia. Call us at 912-409-2031 to confirm service availability for your specific address.",
    cat: 'Service Areas',
  },
  {
    q: 'How long has Masters Fencing been in business?',
    a: "Masters Fencing has served St. Mary's and Camden County for over 20 years. As a locally owned and operated family business, we are deeply invested in every project we accept — our reputation in this community is built one fence at a time through quality work and honest service.",
    cat: 'About',
  },
  {
    q: 'Do you offer military discounts on fencing?',
    a: "Yes. Masters Fencing proudly offers $100 off for military service members and veterans. We also offer $100 off for same-day signing on consultation day. Giving back to the community — including those who served our country — is something we genuinely stand behind.",
    cat: 'Discounts',
  },
];

const FAQSection: React.FC = () => {
  const [openIdx, setOpenIdx] = useState<number | null>(0);

  return (
    <section className="relative bg-[#FAF8F5]" aria-label="Frequently Asked Questions">
      <div className="section-number right-6 top-8">04</div>

      <div className="section-py max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-16 xl:gap-20">

          {/* Left sticky panel */}
          <div className="lg:col-span-2 reveal-left">
            <div className="flex items-center gap-3 mb-6">
              <span className="gold-bar" />
              <span className="text-[#C4A84F] text-[11px] tracking-[0.38em] uppercase font-sans font-medium">FAQ</span>
            </div>
            <h2 className="font-serif text-[3rem] md:text-[3.8rem] text-[#0D0C0B] leading-[0.95] mb-6">
              Your Questions,<br />Answered
            </h2>
            <p className="text-[#0D0C0B]/50 text-base leading-relaxed font-sans mb-10">
              Everything you need to know about fencing with Masters Fencing in St. Mary's, Georgia — costs, timelines, materials, and local availability.
            </p>

            {/* Compact CTA card */}
            <div className="bg-[#0D0C0B] p-8 relative overflow-hidden">
              <div
                className="absolute inset-0 opacity-[0.06] bg-cover bg-center"
                style={{
                  backgroundImage: "url('https://public.readdy.ai/ai/img_res/019103c4383c9e9c9da5d79769afd5f1.jpg')",
                }}
              />
              <div className="relative z-10">
                <h3 className="font-serif text-[1.6rem] text-[#F5F1E8] mb-3">
                  Still have questions?
                </h3>
                <p className="text-[#F5F1E8]/45 text-sm font-sans leading-relaxed mb-7">
                  Call us directly — we respond fast and are glad to walk you through every option at no charge.
                </p>
                <a href="tel:912-409-2031" className="btn-gold mb-4">
                  <i className="fa-solid fa-phone text-[11px]" />
                  912-409-2031
                </a>
                <div className="mt-5 text-[#F5F1E8]/28 text-xs font-sans">
                  Mon–Sat: 6:00 am – 6:00 pm
                </div>
              </div>
            </div>
          </div>

          {/* Right accordion */}
          <div className="lg:col-span-3 space-y-2 reveal-right">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className={`bg-white transition-all duration-300 ${
                  openIdx === i ? 'border-l-[3px] border-[#C4A84F]' : 'border-l-[3px] border-transparent'
                }`}
              >
                <button
                  onClick={() => setOpenIdx(openIdx === i ? null : i)}
                  className="w-full flex items-start justify-between px-6 py-5 text-left cursor-pointer group"
                  aria-expanded={openIdx === i}
                >
                  <div className="flex items-start gap-3 flex-1 pr-4">
                    <span className={`text-[9px] tracking-wider uppercase font-sans px-2 py-1 border shrink-0 mt-0.5 ${
                      openIdx === i ? 'bg-[#C4A84F] border-[#C4A84F] text-[#0D0C0B]' : 'border-[#0D0C0B]/10 text-[#0D0C0B]/35'
                    }`}>
                      {faq.cat}
                    </span>
                    <h3 className={`font-sans text-sm leading-snug transition-colors group-hover:text-[#C4A84F] ${
                      openIdx === i ? 'font-semibold text-[#0D0C0B]' : 'font-medium text-[#0D0C0B]/75'
                    }`}>
                      {faq.q}
                    </h3>
                  </div>
                  <span
                    className={`shrink-0 w-7 h-7 flex items-center justify-center border transition-all duration-300 ${
                      openIdx === i
                        ? 'bg-[#C4A84F] border-[#C4A84F] text-[#0D0C0B] rotate-45'
                        : 'border-[#0D0C0B]/12 text-[#0D0C0B]/35'
                    }`}
                  >
                    <i className="fa-solid fa-plus text-[9px]" />
                  </span>
                </button>

                {openIdx === i && (
                  <div className="px-6 pb-7">
                    <p className="text-[#0D0C0B]/55 text-sm leading-relaxed font-sans border-t border-[#0D0C0B]/5 pt-4">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
